import { useCallback, useEffect, useRef, useState } from "react";
import {
  createPromotionalCode,
  fetchPromotionalCodesPage,
  setPromotionalCodeIsActive,
} from "../actions/owner";

import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

const useOwnerPromotionalCodes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const cursorRef = useRef(null);
  const hasMoreRef = useRef(true);
  const accumRef = useRef([]);

  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      cursorRef.current = null;
      hasMoreRef.current = true;
      accumRef.current = [];

      try {
        const res = await fetchPromotionalCodesPage(null);
        if (cancelled) return;

        accumRef.current = res.rows;
        cursorRef.current = res.cursorAfter;
        hasMoreRef.current = res.hasMore;

        setRows([...accumRef.current]);
        setHasMore(res.hasMore);
      } catch (e) {
        store.dispatch(
          LaunchError(e, false, [], {
            functionName: "loadInitial",
            capture: "exception",
          })
        );
        if (!cancelled) {
          setError(e);
          setRows([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    if (!hasMoreRef.current) return;

    setLoadingMore(true);
    setError(null);
    try {
      const res = await fetchPromotionalCodesPage(cursorRef.current);

      accumRef.current = [...accumRef.current, ...res.rows];
      cursorRef.current = res.cursorAfter;
      hasMoreRef.current = res.hasMore;

      setRows([...accumRef.current]);
      setHasMore(res.hasMore);
    } catch (e) {
      store.dispatch(
        LaunchError(e, false, [], {
          functionName: "loadInitial",
          capture: "exception",
        })
      );
      setError(e);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading]);

  const onCreateCode = useCallback(
    async ({
      code,
      maxRedemptions,
      type,
      tier,
      stripeCode,
      discount,
      discountType,
    }) => {
      await createPromotionalCode({
        code,
        maxRedemptions,
        type,
        tier,
        stripeCode,
        discount,
        discountType,
      });
      const res = await fetchPromotionalCodesPage(null);
      accumRef.current = res.rows;
      cursorRef.current = res.cursorAfter;
      hasMoreRef.current = res.hasMore;
      setRows([...accumRef.current]);
      setHasMore(res.hasMore);
    },
    []
  );

  const onChangeCodeStatus = useCallback(async ({ codeID, isActive }) => {
    await setPromotionalCodeIsActive({ codeID, isActive });
  }, []);

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    onCreateCode,
    onChangeCodeStatus,
    setRows: (updater) => {
      setRows((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        accumRef.current = next;
        return next;
      });
    },
  };
};

export default useOwnerPromotionalCodes;
